import type { Env as EnvConfig, ReadRawEnv } from '@elara/client-core/env-schema';

import { parseEnv, setEnv } from '@elara/client-core/env-schema';
import { DetailedError } from '@elara/core/errors';

/**
 * Web env/config provider (ELARA-694).
 *
 * This is the **web half** of the env seam. It owns the platform-coupled bits the
 * shared contract (`env-schema.ts`) deliberately avoids: reading Vite's
 * `import.meta.env` and tagging the Sentry release. React Native never imports this
 * module — it composes its own `Env` from `parseEnv(Constants.expoConfig.extra)`.
 *
 * The validated {@link Env} type and {@link EnvSchema}/{@link parseEnv} contract are
 * re-exported here so existing `import { Env } from '#env.js'` call sites are
 * unaffected.
 */

export type { RawEnv } from '@elara/client-core/env-schema';
export { EnvSchema, parseEnv } from '@elara/client-core/env-schema';

/** Re-export of the shared {@link EnvConfig} contract type under its familiar name. */
export type Env = EnvConfig;

DetailedError.onError((error) => {
  console.error(error, { ...error.toJSON(), cause: error.cause });
});

/**
 * Web {@link ReadRawEnv} provider: the single point that reads Vite's
 * `import.meta.env`. Isolating the read here keeps the rest of the env machinery
 * platform-neutral.
 */
export const readRawEnv: ReadRawEnv = () => ({ ...import.meta.env });

export const Env: EnvConfig = parseEnv(readRawEnv());

// Inject the composed env into the shared holder (ELARA-700) so platform-agnostic modules
// (SessionState, FeaturesContainer, data utils) can read it via getEnv() without importing this
// web-only provider. React Native does the equivalent from its own bootstrap.
setEnv(Env);

// Side-effect (web only): tag the Sentry release with the build version so
// browser errors map to the deployed bundle.
(typeof process === 'undefined' ? import.meta.env : process.env).SENTRY_RELEASE = Env.APP_VERSION;
